import { z } from 'zod';

/**
 * Plesk import, phase 1 (G9): the API contract for transforming a G8
 * inventory snapshot into panel entities — Plesk customers → clients,
 * service plans → plans, subscriptions → subscriptions. The importer is
 * idempotent (keyed on external references, ADR-0018): a dry run produces
 * the diff plan below, an execute applies it. Workloads, mailboxes, DNS
 * and content do NOT move here — accounts and plan structure only.
 */

// ── Entities & actions ──────────────────────────────────────────────

export const PLESK_IMPORT_ENTITY_TYPES = ['plan', 'client', 'subscription'] as const;
export type PleskImportEntityType = (typeof PLESK_IMPORT_ENTITY_TYPES)[number];

/**
 * What the dry run decided per entity. `conflict` needs a staff decision
 * (link-or-skip / recreate-or-skip); everything else is automatic.
 */
export const PLESK_IMPORT_ACTIONS = ['create', 'update', 'skip', 'conflict'] as const;
export type PleskImportAction = (typeof PLESK_IMPORT_ACTIONS)[number];

/** A field the import will change, rendered for the diff preview. */
export interface PleskFieldChange {
  /** Dotted path, e.g. `email`, `limits.disk_mb`, `overrides.databases`. */
  field: string;
  from: string | null;
  to: string | null;
}

/**
 * A field the import will NOT change although the sides differ: the panel
 * value was edited deliberately since the last import (three-way diff
 * against the imported-state base, ADR-0018). Reported, never overwritten.
 */
export interface PleskHeldField {
  field: string;
  panelValue: string | null;
  pleskValue: string | null;
  /** `both-modified`: Plesk changed too — a genuine divergence. */
  reason: 'panel-modified' | 'both-modified';
}

export const PLESK_CONFLICT_KINDS = ['email-collision', 'name-collision', 'panel-deleted'] as const;
export type PleskConflictKind = (typeof PLESK_CONFLICT_KINDS)[number];

export type PleskConflictChoice = 'link' | 'skip' | 'recreate';

export interface PleskImportConflict {
  kind: PleskConflictKind;
  /** Existing panel entity a collision could link to. */
  candidateId: string | null;
  candidateLabel: string | null;
  /** The choices execute accepts for this conflict. */
  options: PleskConflictChoice[];
  detail: string;
}

export interface PleskImportEntityPlan {
  entityType: PleskImportEntityType;
  /** Plesk-side identity (numeric object id as recorded in the snapshot). */
  externalId: string;
  /** Plesk-side display name (login / plan name / main domain). */
  name: string;
  action: PleskImportAction;
  /** The linked panel entity, when one exists or was matched. */
  panelEntityId: string | null;
  panelLabel: string | null;
  changes: PleskFieldChange[];
  held: PleskHeldField[];
  skipReason: string | null;
  conflict: PleskImportConflict | null;
  /** The resolution applied to this entry (echo of the request). */
  resolution: PleskConflictChoice | null;
  /** Human context: owner links, data gaps, deferred mail limits. */
  notes: string[];
}

// ── Capability evidence ledger ─────────────────────────────────────

/** A Plesk-side object that disappeared since it was last imported. */
export interface PleskExternalDeletion {
  entityType: PleskImportEntityType;
  externalId: string;
  externalName: string | null;
  panelEntityId: string;
  /** Null when the panel entity is gone too. */
  panelLabel: string | null;
}

export interface PleskImportSummary {
  create: number;
  update: number;
  skip: number;
  conflict: number;
}

export interface PleskImportPlanDto {
  snapshotId: string;
  computedAt: string;
  entities: PleskImportEntityPlan[];
  /** Deduplicated v2 capability groups with every source-row occurrence. */
  capabilityLedger: import('./plesk-capabilities.js').PleskCapabilityLedgerDto;
  /**
   * Dry-run replacement guidance for V2-G12's marketplace rows. Decisions are
   * persisted separately and read back here; preview itself remains read-only.
   */
  replacementAdvisor: PleskReplacementAdvisorDto;
  deletions: PleskExternalDeletion[];
  summary: PleskImportSummary;
}

export const pleskCapabilityLedgerQuerySchema = z.object({
  snapshotId: z.string().uuid(),
});
export type PleskCapabilityLedgerQuery = z.infer<typeof pleskCapabilityLedgerQuerySchema>;

// ── Requests ────────────────────────────────────────────────────────

export const pleskImportResolutionSchema = z.object({
  entityType: z.enum(PLESK_IMPORT_ENTITY_TYPES),
  externalId: z.string().trim().min(1).max(100),
  choice: z.enum(['link', 'skip', 'recreate']),
});
export type PleskImportResolution = z.infer<typeof pleskImportResolutionSchema>;

export const pleskImportRequestSchema = z.object({
  snapshotId: z.string().uuid(),
  /** Conflict decisions, keyed by (entityType, externalId). */
  resolutions: z.array(pleskImportResolutionSchema).max(1000).default([]),
});
export type PleskImportRequest = z.infer<typeof pleskImportRequestSchema>;

// ── Installer/site-builder replacement advisor (V2-G12) ───────────

export const PLESK_REPLACEMENT_KEYS = [
  'ext_limit_xovi_max_keywords',
  'upsell_site_builder',
  'ext_permission_plesk_sitejet_access_ecommerce_integration',
  'ext_permission_plesk_sitejet_access_xovi_integration',
  'ext_permission_plesk_sitejet_create_sitejet_site',
  'ext_permission_softaculous_manage_softaculous',
  'ext_permission_softaculous_manage_softaculous_wp',
] as const;
export type PleskReplacementKey = (typeof PLESK_REPLACEMENT_KEYS)[number];

export const PLESK_MARKETPLACE_REPLACEMENT_KEYS = {
  sitejet: [
    'upsell_site_builder',
    'ext_permission_plesk_sitejet_access_ecommerce_integration',
    'ext_permission_plesk_sitejet_access_xovi_integration',
    'ext_permission_plesk_sitejet_create_sitejet_site',
  ],
  xovi: [
    'ext_limit_xovi_max_keywords',
    'ext_permission_plesk_sitejet_access_xovi_integration',
  ],
} as const satisfies Record<'sitejet' | 'xovi', readonly PleskReplacementKey[]>;

export const PLESK_REPLACEMENT_DECISION_TYPES = [
  'blueprint',
  'external-service',
  'skip',
] as const;
export type PleskReplacementDecisionType =
  (typeof PLESK_REPLACEMENT_DECISION_TYPES)[number];

export interface PleskReplacementDecisionDto {
  planExternalId: string;
  planName: string;
  pleskKey: PleskReplacementKey;
  decision: PleskReplacementDecisionType;
  /** Blueprint/external-alternative id; null only for explicit skip. */
  targetId: string | null;
  note: string | null;
  updatedAt: string;
}

export interface PleskReplacementAlternativeDto {
  id: string;
  name: string;
  summary: string;
  maintenanceOwner: string;
  exportImportChecklist: string[];
}

export interface PleskReplacementCapabilityAdviceDto {
  pleskKey: PleskReplacementKey;
  sourceValue: string;
  occurrenceCount: number;
  coveredSubjects: Array<{ entityType: 'plan' | 'subscription'; subject: string }>;
  disposition: import('./plesk-capabilities.js').PleskCapabilityDisposition;
  explanation: string;
  allowedBlueprintIds: string[];
  allowedExternalAlternativeIds: string[];
  decision: PleskReplacementDecisionDto | null;
}

export interface PleskReplacementPlanAdviceDto {
  planExternalId: string;
  planName: string;
  capabilities: PleskReplacementCapabilityAdviceDto[];
}

export interface PleskMarketplaceInventoryEvidenceDto {
  product: 'Softaculous' | 'Sitejet' | 'XOVI';
  status: 'no-active-objects' | 'accounted' | 'review-required';
  activeObjectCount: number | null;
  detail: string;
  evidence: string[];
}

export interface PleskReplacementAdvisorDto {
  summary: {
    affectedPlans: number;
    uniqueKeys: number;
    occurrences: number;
    decisionSlots: number;
    decidedSlots: number;
    decidedOccurrences: number;
    complete: boolean;
  };
  plans: PleskReplacementPlanAdviceDto[];
  blueprints: PleskReplacementAlternativeDto[];
  alternatives: PleskReplacementAlternativeDto[];
  inventoryEvidence: PleskMarketplaceInventoryEvidenceDto[];
  /**
   * False means a known or potentially active object has no visible handling
   * path. Unknown evidence is never silently treated as zero.
   */
  noSilentAbandonment: boolean;
}

export const pleskReplacementDecisionSchema = z
  .object({
    planExternalId: z.string().trim().min(1).max(100),
    pleskKey: z.enum(PLESK_REPLACEMENT_KEYS),
    decision: z.enum(PLESK_REPLACEMENT_DECISION_TYPES),
    targetId: z.string().trim().min(1).max(100).nullable().default(null),
    note: z.string().trim().max(1000).nullable().default(null),
  })
  .superRefine((value, ctx) => {
    if (value.decision === 'skip' && value.targetId !== null) {
      ctx.addIssue({ code: 'custom', path: ['targetId'], message: 'Skip decisions have no target' });
    }
    if (value.decision !== 'skip' && value.targetId === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['targetId'],
        message: 'Blueprint and external-service decisions require a target',
      });
    }
    if (
      value.decision === 'external-service' &&
      (value.note === null || value.note.trim().length === 0)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['note'],
        message: 'External-service decisions require the provider and accountable owner',
      });
    }
  });
export type PleskReplacementDecisionInput = z.infer<
  typeof pleskReplacementDecisionSchema
>;

export const savePleskReplacementDecisionsSchema = z
  .object({
    snapshotId: z.string().uuid(),
    decisions: z.array(pleskReplacementDecisionSchema).min(1).max(500),
  })
  .superRefine((value, ctx) => {
    const seen = new Set<string>();
    value.decisions.forEach((decision, index) => {
      const key = `${decision.planExternalId}:${decision.pleskKey}`;
      if (seen.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['decisions', index],
          message: 'Each Plesk plan/key decision may appear only once',
        });
      }
      seen.add(key);
    });
  });
export type SavePleskReplacementDecisionsInput = z.infer<
  typeof savePleskReplacementDecisionsSchema
>;

// ── Execution results ───────────────────────────────────────────────

export const PLESK_IMPORT_OUTCOMES = ['created', 'updated', 'linked', 'skipped'] as const;
export type PleskImportOutcome = (typeof PLESK_IMPORT_OUTCOMES)[number];

export interface PleskImportResultEntry {
  entityType: PleskImportEntityType;
  externalId: string;
  name: string;
  outcome: PleskImportOutcome;
  panelEntityId: string | null;
  detail: string | null;
}

export type PleskImportResultSummary = Partial<Record<PleskImportOutcome, number>>;

export interface PleskImportRunDto {
  id: string;
  /** Null when the source snapshot was deleted after the run. */
  snapshotId: string | null;
  status: 'completed' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  summary: PleskImportResultSummary | null;
  error: string | null;
}

export interface PleskImportRunDetailDto extends PleskImportRunDto {
  plan: PleskImportPlanDto | null;
  results: PleskImportResultEntry[] | null;
}

export interface PleskImportExecuteResponse {
  run: PleskImportRunDto;
  results: PleskImportResultEntry[];
  /** The plan as recomputed at execute time (resolutions applied). */
  plan: PleskImportPlanDto;
}
